import { ErrorHandler, importProvidersFrom } from '@angular/core';
import * as Sentry from '@sentry/angular';

import { environment } from './environments/environment';
import { httpInterceptorProviders } from './app/http-interceptors';
import { KaisatsuErrorHandler } from './app/shared/kaisatsu-error-handler';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { AppRoutingModule } from './app/app-routing.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { SpenFormFieldModule, SpenModule } from '@classi/spen';
import { ReactiveFormsModule } from '@angular/forms';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth } from '@angular/fire/auth';
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  browserPopupRedirectResolver,
} from 'firebase/auth';
import { getApp } from 'firebase/app';
import { AppComponent } from './app/app.component';

/**
 *
 * @see https://mswjs.io/docs/recipes/deferred-mounting
 *
 */

async function prepare() {
  if (environment.kaisatsuApiBaseUrl === '') {
    const { worker } = await import('./mocks/browser');
    return await worker.start();
  }
  return;
}

if (environment.sentry !== null) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    release: environment.sentry.release,
    integrations: [Sentry.browserTracingIntegration({})],
    tracesSampleRate: 0.001,
    sendDefaultPii: false, // v10でのIP収集制御を明示的に設定
    ignoreErrors: [
      /\bCan't find variable: removeAllHighlightsForSB\b/,
      /\bCan't find variable: MyApp_RemoveAllHighlights\b/,
      /\bCan't find variable: WKWebView_RemoveAllHighlights\b/,
      /\bonSvFinishLoading is not defined\b/,
      'window.onorientationchange is not a function',
      // GAから出てるエラー
      /^L$/,
      /^N$/,
    ],
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],
  });
}

prepare()
  .then(() => {
    return bootstrapApplication(AppComponent, {
      providers: [
        importProvidersFrom(
          BrowserModule,
          AppRoutingModule,
          SpenFormFieldModule,
          SpenModule,
          ReactiveFormsModule
        ),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() =>
          initializeAuth(getApp(), {
            persistence: [
              indexedDBLocalPersistence, // 1st try
              browserLocalPersistence, // 2nd try (localStorage)
              inMemoryPersistence, // 最終 fallback
            ],
            popupRedirectResolver: browserPopupRedirectResolver,
          })
        ),
        httpInterceptorProviders,
        {
          provide: ErrorHandler,
          useClass: KaisatsuErrorHandler,
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
      ],
    });
  })
  .catch((err) => console.error(err));
